import { useEffect, useState } from "react";
import api, { brl } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, TrendingUp, TrendingDown } from "lucide-react";

const EMPTY = { type: "receita", category: "", description: "", amount: 0, payment_method: "dinheiro" };

export default function Financial() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ receita: 0, despesa: 0, lucro: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/financial/transactions").then((r) => setItems(r.data));
    api.get("/financial/summary").then((r) => setSummary(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/financial/transactions", form);
      toast.success("Lançamento registrado!"); setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Erro"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir lançamento?")) return;
    await api.delete(`/financial/transactions/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="financial-page">
      <header className="flex justify-between items-end">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">Fluxo de Caixa</div>
          <h1 className="font-display text-4xl font-bold">Financeiro</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="fin-new-btn"
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
          <Plus size={16} /> Novo Lançamento
        </button>
      </header>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1"><TrendingUp size={14} className="text-emerald-500"/> Receitas</div>
          <div className="font-display text-3xl font-bold text-emerald-600">{brl(summary.receita)}</div>
        </div>
        <div className="bg-white border border-slate-200 p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1"><TrendingDown size={14} className="text-red-500"/> Despesas</div>
          <div className="font-display text-3xl font-bold text-red-600">{brl(summary.despesa)}</div>
        </div>
        <div className="bg-[#0A1628] text-white p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-2">Lucro Líquido</div>
          <div className="font-display text-3xl font-bold">{brl(summary.lucro)}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Categoria</th>
              <th className="text-left p-3">Descrição</th>
              <th className="text-left p-3">Pagamento</th>
              <th className="text-right p-3">Valor</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fin-row-${t.id}`}>
                <td className="p-3 text-slate-600">{(t.date || t.created_at || "").slice(0, 10)}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded-sm ${t.type === "receita" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {t.type === "receita" ? "RECEITA" : "DESPESA"}
                  </span>
                </td>
                <td className="p-3">{t.category}</td>
                <td className="p-3 text-slate-700">{t.description}</td>
                <td className="p-3 text-slate-500 capitalize">{t.payment_method}</td>
                <td className={`p-3 text-right font-semibold ${t.type === "receita" ? "text-emerald-600" : "text-red-600"}`}>
                  {t.type === "receita" ? "+" : "-"}{brl(t.amount)}
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => remove(t.id)} className="p-1 hover:bg-red-100 text-red-500 rounded-sm"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="7" className="p-12 text-center text-slate-500">Nenhum lançamento ainda.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm max-w-lg w-full p-6" data-testid="fin-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl font-bold">Novo Lançamento</h2>
              <button onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})}
                data-testid="fin-type" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                <option value="receita">Receita</option>
                <option value="despesa">Despesa</option>
              </select>
              <input type="text" placeholder="Categoria" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                data-testid="fin-category" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <input type="text" placeholder="Descrição" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                data-testid="fin-description" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <input type="number" placeholder="Valor" value={form.amount} onChange={(e) => setForm({...form, amount: Number(e.target.value)})}
                data-testid="fin-amount" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <select value={form.payment_method} onChange={(e) => setForm({...form, payment_method: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                <option value="dinheiro">Dinheiro</option>
                <option value="pix">Pix</option>
                <option value="cartao_credito">Cartão Crédito</option>
                <option value="cartao_debito">Cartão Débito</option>
                <option value="boleto">Boleto</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">Cancelar</button>
              <button onClick={save} data-testid="fin-save-btn" className="bg-[#2563EB] text-white px-4 py-2 rounded-sm text-sm font-semibold">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
